import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, PalmtreeIcon, Plus, XCircle } from "lucide-react";

export default function MyPtoPage() {
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pto, tps] = await Promise.all([api.get("/pto/requests"), api.get("/pto/types")]);
      setItems(pto.data.items);
      setTypes(tps.data.items);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    try {
      await api.post(`/pto/requests/${id}/cancel`);
      toast.success("Canceled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="my-pto-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">My PTO</div>
          <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Paid time off</h1>
          <p className="mt-2 text-muted-foreground">Request vacation, sick, or holiday hours. Approvals happen through the same approver as your timesheets.</p>
        </div>
        <Button onClick={() => setCreating(true)} disabled={types.length === 0} data-testid="pto-new-btn"><Plus className="h-4 w-4" /> Request PTO</Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-secondary/10 text-secondary"><PalmtreeIcon className="h-8 w-8" /></div>
            <div className="mt-4 font-medium">No requests yet</div>
            <p className="mt-1 text-sm text-muted-foreground">Ready for time off? Tap Request PTO.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {items.map((r) => (
            <Card key={r.id} data-testid={`pto-${r.id}`}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <div className="flex items-center gap-3">
                  <span className="inline-block h-3 w-3 rounded" style={{ backgroundColor: r.pto_type_color || "#8b95a7" }} />
                  <div>
                    <CardTitle className="text-base">{r.pto_type_label}</CardTitle>
                    <CardDescription>{r.start_date}{r.start_date !== r.end_date && ` → ${r.end_date}`} · {r.hours_per_day}h/day</CardDescription>
                  </div>
                </div>
                <StatusPill status={r.status} />
              </CardHeader>
              <CardContent className="flex flex-wrap items-center justify-between gap-3">
                {r.reason && <div className="text-sm italic text-muted-foreground">Reason: {r.reason}</div>}
                {r.rejection_reason && <div className="text-sm italic text-destructive">Rejected: {r.rejection_reason}</div>}
                {(r.status === "pending" || (r.status === "approved" && new Date(r.end_date) >= new Date(new Date().toDateString()))) && (
                  <Button variant="ghost" size="sm" onClick={() => cancel(r.id)} data-testid={`pto-cancel-${r.id}`}>
                    <XCircle className="h-3 w-3" /> Cancel
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <NewPtoDialog open={creating} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} types={types} />
    </div>
  );
}

function NewPtoDialog({ open, onClose, onCreated, types }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ pto_type_id: "", start_date: today, end_date: today, hours_per_day: 8, reason: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ pto_type_id: types[0]?.id || "", start_date: today, end_date: today, hours_per_day: 8, reason: "" }); }, [open, types, today]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/pto/requests", { ...form, reason: form.reason || undefined });
      toast.success("PTO requested — approver notified");
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="pto-new-dialog">
        <DialogHeader><DialogTitle>Request PTO</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={form.pto_type_id} onValueChange={(v) => setForm({ ...form, pto_type_id: v })}>
              <SelectTrigger data-testid="pto-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.label} ({t.code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="pto-start" /></div>
            <div className="space-y-2"><Label>End date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="pto-end" /></div>
          </div>
          <div className="space-y-2"><Label>Hours per day</Label><Input type="number" min={1} max={24} value={form.hours_per_day} onChange={(e) => setForm({ ...form, hours_per_day: Number(e.target.value) })} data-testid="pto-hours" /></div>
          <div className="space-y-2"><Label>Reason (optional)</Label><Textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="pto-reason" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !form.pto_type_id} data-testid="pto-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: "bg-blue-500/15 text-blue-700",
    approved: "bg-success/15 text-success",
    rejected: "bg-destructive/15 text-destructive",
    canceled: "bg-muted text-muted-foreground",
  };
  return <Badge className={`rounded-full capitalize ${map[status] || map.canceled}`}>{status}</Badge>;
}
